import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { firebaseCategoriesService, firebaseWinesService } from '../../services/firebaseService';
import { mockCategoriesApi, mockWinesApi } from '../../services/mockData';
// Eliminado: import { categoriesApi, winesApi } from '../../services/api';
import { toast } from 'react-hot-toast';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import BackButton from '../../components/common/BackButton';
import { firebaseSectionService } from '../../services/firebaseSectionService';
import Switch from '@mui/material/Switch';

const AdminCategories = () => {
  const [open, setOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, category: null });

  const queryClient = useQueryClient();

  // Query para obtener categorías usando Firebase
  const { data: categories = [], isLoading, error } = useQuery({
    queryKey: ['firebaseCategories'],
    queryFn: async () => {
      try {
        console.log('📂 AdminCategories: Consultando categorías desde Firebase...');
        const response = await firebaseCategoriesService.getAll();
        console.log('✅ Categorías obtenidas de Firebase:', response.data.data);
        // Asegurar que siempre retornamos un array
        const categoriesData = response.data.data;
        return Array.isArray(categoriesData) ? categoriesData : [];
      } catch (error) {
        console.warn('⚠️ Error con Firebase, usando API tradicional...', error);
        // Solo usar mock si Firebase falla
        const response = await mockCategoriesApi.getAll();
        const categoriesData = response.data.data;
        return Array.isArray(categoriesData) ? categoriesData : [];
      }
    }
  });

  // Query para obtener vinos usando Firebase (para verificar dependencias)
  const { data: wines = [] } = useQuery({
    queryKey: ['firebaseWines'],
    queryFn: async () => {
      try {
        console.log('🍷 AdminCategories: Consultando vinos desde Firebase...');
        const response = await firebaseWinesService.getAll();
        const winesData = response.data.data;
        return Array.isArray(winesData) ? winesData : [];
      } catch (error) {
        console.warn('⚠️ Error con Firebase wines, usando API tradicional...', error);
        // Solo usar mock si Firebase falla
        const response = await mockWinesApi.getAll();
        const winesData = response.data.data;
        return Array.isArray(winesData) ? winesData : [];
      }
    }
  });

  const { data: sections = [], isLoading: isLoadingSections } = useQuery({
    queryKey: ['firebaseSectionsAll'],
    queryFn: async () => {
      return await firebaseSectionService.getAllSections();
    }
  });

  // Mutation para crear categoría
  const createMutation = useMutation({
    mutationFn: async (data) => {
      try {
        console.log('🔥 AdminCategories: Creando categoría en Firebase...');
        const result = await firebaseCategoriesService.create(data);
        console.log('✅ Categoría creada en Firebase:', result.data.data.id);
        return result;
      } catch (error) {
        console.warn('⚠️ Error creando en Firebase, usando mock...', error);
        return await mockCategoriesApi.create(data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['firebaseCategories']);
      setOpen(false);
      resetForm();
      toast.success('Categoría creada exitosamente en Firebase');
    },
    onError: (error) => {
      toast.error(error.message || 'Error al crear la categoría');
    }
  });

  // Mutation para actualizar categoría
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      try {
        console.log('🔄 AdminCategories: Actualizando categoría en Firebase...', id);
        const result = await firebaseCategoriesService.update(id, data);
        console.log('✅ Categoría actualizada en Firebase:', id);
        return result;
      } catch (error) {
        console.warn('⚠️ Error actualizando en Firebase, usando mock...', error);
        return await mockCategoriesApi.update(id, data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['firebaseCategories']);
      setOpen(false);
      resetForm();
      toast.success('Categoría actualizada exitosamente en Firebase');
    },
    onError: (error) => {
      toast.error(error.message || 'Error al actualizar la categoría');
    }
  });

  // Mutation para eliminar categoría
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      try {
        console.log('🗑️ AdminCategories: Eliminando categoría de Firebase...', id);
        const result = await firebaseCategoriesService.delete(id);
        console.log('✅ Categoría eliminada de Firebase:', id);
        return result;
      } catch (error) {
        console.warn('⚠️ Error eliminando de Firebase, usando mock...', error);
        return await mockCategoriesApi.delete(id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['firebaseCategories']);
      queryClient.invalidateQueries(['firebaseWines']);
      setDeleteDialog({ open: false, category: null });
      toast.success('Categoría eliminada exitosamente de Firebase');
    },
    onError: (error) => {
      toast.error(error.message || 'Error al eliminar la categoría');
      setDeleteDialog({ open: false, category: null });
    }
  });

  const updateCategoryVisibility = useMutation({
    mutationFn: async ({ id, visible }) => {
      const boolVisible = !!visible;
      console.log(`[DEBUG] Actualizando visibilidad de categoría ${id}:`, boolVisible);
      return await firebaseCategoriesService.update(id, { visible: boolVisible });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['firebaseCategories']);
      toast.success('Visibilidad de categoría actualizada');
    }
  });

  const updateSectionVisibility = useMutation({
    mutationFn: async ({ id, visible }) => {
      return await firebaseSectionService.updateSection(id, { visible });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['firebaseSectionsAll']);
      toast.success('Visibilidad de sección actualizada');
    }
  });

  const resetForm = () => {
    setFormData({ name: '', description: '' });
    setEditingCategory(null);
  };

  const handleOpenDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name,
        description: category.description
      });
    } else {
      resetForm();
    }
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    resetForm();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    
    if (!formData.description.trim()) {
      toast.error('La descripción es requerida');
      return;
    }

    if (editingCategory) {
      updateMutation.mutate({
        id: editingCategory.id,
        data: formData
      });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDeleteClick = (category) => {
    setDeleteDialog({ open: true, category });
  };

  const handleConfirmDelete = () => {
    if (deleteDialog.category) {
      deleteMutation.mutate(deleteDialog.category.id);
    }
  };

  const getWineCountByCategory = (categoryId) => {
    // Asegurar que wines es un array antes de filtrar
    if (!Array.isArray(wines)) {
      return 0;
    }
    return wines.filter(wine => wine.categoryId === categoryId).length;
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <Box>
      <BackButton />
      
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CategoryIcon />
          Gestión de Categorías
        </Typography>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Nueva Categoría
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error al cargar las categorías
        </Alert>
      )}

      {/* Tabla de categorías */}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>Descripción</TableCell>
              <TableCell>Visible</TableCell>
              <TableCell align="center">Productos</TableCell>
              <TableCell align="center">Fecha Creación</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.isArray(categories) && categories.map((category) => {
              const wineCount = getWineCountByCategory(category.id);
              return (
                <TableRow key={category.id}>
                  <TableCell>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {category.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {category.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={!!category.visible}
                      onChange={e => {
                        const checked = !!e.target.checked;
                        console.log(`[DEBUG] Switch cambiado para categoría ${category.id}:`, checked);
                        updateCategoryVisibility.mutate({ id: category.id, visible: checked });
                      }}
                      color="primary"
                      inputProps={{ 'aria-label': 'Visibilidad categoría' }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={`${wineCount} productos`}
                      variant="outlined"
                      color={wineCount > 0 ? "primary" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    {category.createdAt ? new Date(category.createdAt.seconds ? category.createdAt.seconds * 1000 : category.createdAt).toLocaleDateString() : 'N/A'}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Editar">
                      <IconButton
                        onClick={() => handleOpenDialog(category)}
                        color="primary"
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={wineCount > 0 ? "No se puede eliminar: tiene productos asociados" : "Eliminar"}>
                      <span>
                        <IconButton
                          onClick={() => handleDeleteClick(category)}
                          color="error"
                          size="small"
                          disabled={wineCount > 0}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {(!Array.isArray(categories) || categories.length === 0) && !isLoading && (
        <Box textAlign="center" py={4}>
          <Typography variant="body1" color="text.secondary">
            No hay categorías registradas
          </Typography>
        </Box>
      )}

      <Box mt={6}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Secciones personalizadas visibles en la página principal
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Descripción</TableCell>
                <TableCell>Visible</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sections.map(section => (
                <TableRow key={section.id}>
                  <TableCell>{section.name}</TableCell>
                  <TableCell>{section.description}</TableCell>
                  <TableCell>
                    <Switch
                      checked={!!section.visible}
                      onChange={e => updateSectionVisibility.mutate({ id: section.id, visible: e.target.checked })}
                      color="primary"
                      inputProps={{ 'aria-label': 'Visibilidad sección' }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Dialog para crear/editar categoría */}
      <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {editingCategory ? 'Editar Categoría' : 'Nueva Categoría'}
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Nombre"
              type="text"
              fullWidth
              variant="outlined"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Descripción"
              type="text"
              fullWidth
              variant="outlined"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} color="inherit">
              Cancelar
            </Button>
            <Button 
              type="submit" 
              variant="contained"
              color="secondary"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {editingCategory ? 'Actualizar' : 'Crear'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog de confirmación para eliminar */}
      <Dialog 
        open={deleteDialog.open} 
        onClose={() => setDeleteDialog({ open: false, category: null })}
      >
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que deseas eliminar la categoría "{deleteDialog.category?.name}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialog({ open: false, category: null })}
            color="inherit"
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminCategories; 